import React, { useState, useContext } from 'react';
import Modal from 'react-responsive-modal';
import PropTypes from 'prop-types';
import axios from 'axios';

import Submit from '../common/PrimaryButton';
import DatePicker from '../common/DatePicker';
import BindRequestValidator from '../../helpers/validators/bind-request.validator';
import Error from '../common/Error';
import File from '../common/File';
import findErrorField from '../../helpers/serverError';
import alertActions from '../../actions/alert.actions';
import AlertDispatch from '../../contexts/alert.dispatch';
import RadioButton from '../common/RadioButton';

import style from './BindRequest.module.css';

import 'react-responsive-modal/styles.css';
import '../common/ResponsiveModal.css';

const BindRequest = React.memo(
  ({ opened, onClose, busName, lob, carrier, premium, quoteId, clientId }) => {
    const alertDispatch = useContext(AlertDispatch);
    const [effDate, setEffDate] = useState();
    const [file, setFile] = useState();
    const handleEffectiveDateChange = date => {
      setEffDate(date);
    };

    const handleRadioChange = event => {
      switch (event.target.value) {
        case 'yes':
          setMultiYearPolicy({
            Yes: true,
          });
          break;
        case 'no':
          setMultiYearPolicy({
            Yes: false,
          });
          break;
        case 'agencyBill':
          setSelectedBillingType({
            DirectBill: false,
            AgencyBill: true,
          });
          break;
        case 'directBill':
          setSelectedBillingType({
            DirectBill: true,
            AgencyBill: false,
          });
          break;
        default:
          break;
      }
    };

    const [effError, setEffError] = useState('');
    const [sizeError, setSizeError] = useState('');
    const [selectedBillingType, setSelectedBillingType] = useState({
      DirectBill: false,
      AgencyBill: false,
    });
    const [multiYearPolicy, setMultiYearPolicy] = useState({
      Yes: undefined,
    });

    const closeIcon = (
      <g fill="none" fillRule="evenodd">
        <path
          fill="#231F20"
          d="M16 1.28C7.883 1.28 1.28 7.883 1.28 16S7.883 30.72 16 30.72 30.72 24.117 30.72 16 24.117 1.28 16 1.28zM16 32C7.177 32 0 24.823 0 16S7.177 0 16 0s16 7.177 16 16-7.177 16-16 16z"
        />
        <g fill="#000">
          <path d="M22.165 22.534a.63.63 0 0 1-.446-.185L9.809 10.44a.632.632 0 1 1 .893-.893l11.91 11.91a.632.632 0 0 1-.447 1.078" />
          <path d="M10.256 22.534a.633.633 0 0 1-.447-1.078l11.91-11.91a.632.632 0 1 1 .893.894l-11.91 11.91a.63.63 0 0 1-.446.184" />
        </g>
      </g>
    );

    const handleSizeOverMax = () => {
      setSizeError(
        'The file could not be uploaded because the file size exceeds the maximum size permitted of 75MB.',
      );
    };

    const handleSelectFile = newFile => {
      setSizeError('');
      setFile(newFile);
    };

    const handleClose = () => {
      onClose(false);
    };

    const handleSubmit = event => {
      const handleSuccess = () => {
        onClose(true);
        alertDispatch({
          type: alertActions.SUCCESS,
          message:
            'Thank you for your request. A member of your dedicated team will respond within 1 business day.',
        });
      };

      const handleError = ({ response }) => {
        if (response && response.data && response.data.ValidationErrors) {
          const effDateError = findErrorField(
            response.data.ValidationErrors,
            'EffDate',
          );

          setEffError(effDateError ? effDateError.Messages[0] : '');
        } else {
          alertDispatch({
            type: alertActions.ERROR,
            message: 'Something went wrong. Please try again',
          });
          onClose(false);
        }
      };

      event.preventDefault();

      const validation = BindRequestValidator.validate({
        effDate,
      });

      setEffError(validation.effDate.message);

      if (validation.isValid) {
        axios
          .post(
            `${process.env.REACT_APP_BASE_URL}panels/quotes/${quoteId}/hub-bind-request/bff`,
            {
              EffectiveDate: effDate,
              ClientId: clientId,
              File: file,
              BillingType: selectedBillingType.AgencyBill
                ? 'Agency Bill'
                : selectedBillingType.DirectBill
                ? 'Direct Bill'
                : 'Not Selected',
              MultiYearPolicy: multiYearPolicy.Yes
                ? 'Yes'
                : multiYearPolicy.Yes === false
                ? 'No'
                : 'Not Selected',
            },
            {
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
              },
            },
          )
          .then(handleSuccess)
          .catch(handleError);
      }
    };

    return (
      <Modal
        center
        open={opened}
        onClose={handleClose}
        classNames={{ modal: style.modalContent }}
        styles={{ closeButton: { outline: 0, cursor: 'pointer' } }}
        closeIconSvgPath={closeIcon}
      >
        <form className={style.main} onSubmit={handleSubmit}>
          <div className={style.clientInfo}>
            <span className={style.label}>Client&apos;s Business Name:</span>
            <span className={style.value}>{busName}</span>
          </div>
          <div className={style.clientInfo}>
            <span className={style.label}>Line of Business:</span>
            <span className={style.value}>{lob}</span>
          </div>
          <div className={style.clientInfo}>
            <span className={style.label}>Carrier:</span>
            <span className={style.value}>{carrier}</span>
          </div>
          <div className={style.clientInfo}>
            <span className={style.label}>Premium:</span>
            <span className={style.value}>{premium}</span>
          </div>
          <div className={`form-group ${style.clientInfo}`}>
            <div className={style.label}>
              Billing: (Arch only offers agency bill)
            </div>
            <div className={style.radioRow}>
              <RadioButton
                name="billing"
                value="directBill"
                label="Direct bill"
                checked={selectedBillingType.DirectBill}
                labelStyles={radioButtonLabelStyle}
                onChange={handleRadioChange}
              />
              <RadioButton
                name="billing"
                value="agencyBill"
                label="Agency bill"
                checked={selectedBillingType.AgencyBill}
                labelStyles={radioButtonLabelStyle}
                onChange={handleRadioChange}
              />
            </div>
          </div>
          <div className={`form-group ${style.clientInfo}`}>
            <div className={style.label}>
              Multi-Year Policy: If offered, do you want the multi-year policy?
            </div>
            <div className={style.radioRow}>
              <RadioButton
                name="multiYearPolicy"
                value="yes"
                label="Yes"
                checked={multiYearPolicy.Yes}
                labelStyles={radioButtonLabelStyle}
                onChange={handleRadioChange}
              />
              <RadioButton
                name="multiYearPolicy"
                value="no"
                label="No"
                checked={multiYearPolicy.Yes === false}
                labelStyles={radioButtonLabelStyle}
                onChange={handleRadioChange}
              />
            </div>
          </div>
          <div className={style.clientInfo} style={{ width: '500px' }}>
            <span className={style.label}>Effective Date:</span>
            <DatePicker
              minDate={new Date()}
              selectedDate={effDate}
              onChange={handleEffectiveDateChange}
            />
            <Error error={effError} />
          </div>
          <div className={style.clientInfo} style={{ width: '500px' }}>
            <span className={style.label}>
              Please attach signed application (optional):{' '}
            </span>
            <File
              clientId={clientId}
              handleSelectFile={handleSelectFile}
              maxFileSize={75000000}
              handleFileSizeOverMax={handleSizeOverMax}
            />
            <p />
            <Error error={sizeError} />
          </div>
          <div className={style.terms}>
            <h3 className={style.header}>Confirm Terms and Conditions</h3>
            <p>
              All carrier quotes and proposals are subject to verification of
              eligibility. Due to unique and varying characteristics of each
              risk, binding may require additional information and may impact
              pricing.
            </p>
            <p>
              To obtain insurance quotes or any other services through Insureon,
              you must agree to the following terms and conditions:
            </p>
            <ul>
              <li>
                You agree to provide accurate, complete and current information
                about your client’s business exposures through any applications
                or documentation you submit.
              </li>
              <li>
                By submitting an application for insurance on behalf of your
                client to Insureon, you are representing that you have obtained
                your client’s consent to submit the application and that you
                have authority to act on behalf of your client, affirming your
                client’s representation of all information submitted is
                accurate, complete and current.
              </li>
              <li>
                You further agree to provide updated information should there be
                any changes between application and binding. Insureon is not
                responsible for transactions resulting from information you
                provide about yourself or your client that is not accurate, not
                current, incomplete or fraudulent.
              </li>
            </ul>
            <p>
              Any person who knowingly and with intent to defraud any insurance
              company or other person files an application for insurance or
              statement of claim containing materially false information, or
              conceals for the purpose of misleading, information concerning any
              fact material thereto, commits a fraudulent insurance act, which
              is a crime, and shall also be subject to civil penalty not to
              exceed five thousand dollars and the stated value of the claim for
              each such violation.
            </p>
            <p>
              To proceed, click “Submit Bind Request” to agree to the terms and
              conditions. If you do not agree to the terms and conditions, or
              want to go back, please click “Cancel”.
            </p>
          </div>
          <div className={style.actions}>
            <Submit
              styles={{ width: '230px', marginLeft: '10px', padding: '0' }}
              text="Submit Bind Request"
            />
            <button type="button" onClick={onClose} className={style.cancelBtn}>
              Cancel
            </button>
          </div>
        </form>
      </Modal>
    );
  },
);

const radioButtonLabelStyle = {
  flex: '1',
  paddingTop: '16px',
  paddingBottom: '16px',
  marginRight: '16px',
  marginLeft: '10px',
  fontWeight: '400',
  lineHeight: '1.5',
  color: '#000',
  fontSize: '15px',
};

BindRequest.propTypes = {
  opened: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  quoteId: PropTypes.string.isRequired,
};

export default BindRequest;
